import React, { useState, ChangeEvent, useEffect, useMemo, useRef } from 'react';
import demoImage from '../../../assets/images/no2.png';
import { checkIcon2, menuIcon2, searchFontIcon } from 'constants/ui/svg';
import ColorPicker from 'react-best-gradient-color-picker';
import ReactDOM from 'react-dom';
import { NoteStyleConfig } from 'models';
import { Modal } from 'antd';
import background from '../../../assets/images/bg.png';
import { rgbToHex } from 'helpers/functionHelper';
import { t } from 'i18next';
interface ModalTextEditorProps {
  onClose: () => void;
  initialNote?: string;
  initialStyleConfig?: Partial<NoteStyleConfig>;
  onSave: (data: { note: string; styleConfig: NoteStyleConfig }) => void;
  previewImage?: string;
  isNote?: boolean; //check xem co phai edit note hay khong
}

const ModalTextEditor: React.FC<ModalTextEditorProps> = ({
  onClose,
  initialNote = '',
  initialStyleConfig = {},
  onSave,
  previewImage,
  isNote = true,
}) => {
  const fonts = FONTS.sort();

  const [text, setText] = useState<string>(initialNote || '');
  const [font, setFont] = useState<string>(initialStyleConfig.font || 'NotoSansNormal');
  const [fontSize, setFontSize] = useState<number>(initialStyleConfig.font_size || 20);
  const [color, setColor] = useState<string>(initialStyleConfig.color || '#FEC84B');
  const [strokeColor, setStrokeColor] = useState<string>(
    initialStyleConfig.stroke_color || '#ffffff'
  );
  const [strokeWidth, setStrokeWidth] = useState<number>(initialStyleConfig.stroke_width || 2);
  const [searchFont, setSearchFont] = useState('');
  const [isOpenFontDropDown, setIsOpenFontDropDown] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showColorStrokePicker, setShowColorStrokePicker] = useState(false);

  const isMobileDevice = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent);
  }, []);

  // Track the displayed image rectangle inside the preview so text follows scaling
  const previewRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const colorButtonRef = useRef<HTMLDivElement>(null);
  const colorPickerStrokeRef = useRef<HTMLDivElement>(null);
  const colorButtonStrokeRef = useRef<HTMLDivElement>(null);

  const [imgNaturalSize, setImgNaturalSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [previewSize, setPreviewSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  useEffect(() => {
    const handleResize = () => {
      if (!previewRef.current) return;
      const rect = previewRef.current.getBoundingClientRect();
      setPreviewSize({ w: rect.width, h: rect.height });
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const displayedRect = useMemo(() => {
    const containerW = previewSize.w;
    const containerH = previewSize.h;
    const naturalW = imgNaturalSize.w;
    const naturalH = imgNaturalSize.h;
    if (!containerW || !containerH || !naturalW || !naturalH) {
      return { left: 0, top: 0, width: 0, height: 0 };
    }
    const scale = Math.min(containerW / naturalW, containerH / naturalH);
    const width = naturalW * scale;
    const height = naturalH * scale;
    const left = (containerW - width) / 2;
    const top = (containerH - height) / 2;
    return { left, top, width, height };
  }, [previewSize, imgNaturalSize]);

  const scaleFactor = useMemo(() => {
    if (!displayedRect.width || !imgNaturalSize.w) return 1;
    return displayedRect.width / imgNaturalSize.w; // tỉ lệ ảnh đang hiển thị
  }, [displayedRect.width, imgNaturalSize.w]);

  const scaledFont = Math.max(8, Math.round(fontSize * scaleFactor));
  const scaledStroke = Math.max(0, strokeWidth * scaleFactor);
  const scaledHostCollectionFont = Math.min(Math.round(18 * scaleFactor), 18);
  const [isMobileLayout, setIsMobileLayout] = useState(false);

  useEffect(() => {
    const updateLayout = () => {
      if (typeof window === 'undefined') return;
      setIsMobileLayout(window.innerWidth < 768);
    };

    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, []);

  const basePreviewContainerClass =
    'w-full border-b-1 border-gray-400 shadow-sm md:border-0 md:w-2/3 bg-gray-100 relative mx-auto md:rounded-md';
  const defaultPreviewSizingClass =
    'aspect-[4/5] md:aspect-auto h-[45vh] md:h-auto md:max-h-[600px] overflow-hidden';
  const previewContainerClass = useMemo(() => {
    if (isNote) {
      return `${basePreviewContainerClass} ${defaultPreviewSizingClass}`;
    }

    if (isMobileLayout) {
      return `${basePreviewContainerClass} flex flex-col gap-2 items-center justify-start h-auto overflow-visible`;
    }

    return `${basePreviewContainerClass} ${defaultPreviewSizingClass}`;
  }, [isNote, isMobileLayout]);

  const resolvedPreviewWidth = useMemo(() => {
    if (isMobileLayout) return '100%';
    if (displayedRect.width) return `${displayedRect.width}px`;
    if (previewSize.w) return `${previewSize.w}px`;
    return '100%';
  }, [displayedRect.width, previewSize.w, isMobileLayout]);

  function useFloatingPicker(triggerRef: React.RefObject<HTMLElement>, isVisible: boolean) {
    const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

    useEffect(() => {
      if (isVisible && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const pickerWidth = 200;
        const pickerHeight = 240;
        let left = rect.left + rect.width / 2 - pickerWidth / 2;

        let top = rect.bottom + 8;
        if (top + pickerHeight > window.innerHeight) {
          top = rect.top - pickerHeight + 8;
        }
        if (left < 10) left = 10;
        if (left + pickerWidth > window.innerWidth - 10) {
          left = window.innerWidth - pickerWidth - 10;
        }

        setPosition({ top, left });
      }
    }, [isVisible, triggerRef]);

    return position;
  }

  const colorPosition = useFloatingPicker(colorButtonRef, showColorPicker);
  const strokePosition = useFloatingPicker(colorButtonStrokeRef, showColorStrokePicker);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        showColorPicker &&
        colorPickerRef.current &&
        colorButtonRef.current &&
        !colorPickerRef.current.contains(e.target as Node) &&
        !colorButtonRef.current.contains(e.target as Node)
      ) {
        setShowColorPicker(false);
      }

      if (
        showColorStrokePicker &&
        colorPickerStrokeRef.current &&
        colorButtonStrokeRef.current &&
        !colorPickerStrokeRef.current.contains(e.target as Node) &&
        !colorButtonStrokeRef.current.contains(e.target as Node)
      ) {
        setShowColorStrokePicker(false);
      }
    };

    if (showColorPicker || showColorStrokePicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showColorPicker, showColorStrokePicker]);

  // Dropdown
  useEffect(() => {
    const handlePointerDown = (e: PointerEvent) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target as Node)) {
        setIsOpenFontDropDown(false);
      }
    };

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpenFontDropDown(false);
    };

    document.addEventListener('pointerdown', handlePointerDown, { capture: true });
    document.addEventListener('keydown', handleKey);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, { capture: true });
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  // Save handler
  const hasChanges = useMemo(() => {
    const currentText = text.trim();
    const initialText = (initialNote || '').trim();

    // Trường hợp xóa: ban đầu có text, hiện tại trống => coi là đã thay đổi (để ngoài xử lý delete)
    if (initialText && !currentText) return true;

    // Nếu hiện tại không có text và ban đầu cũng không có => không coi là thay đổi hợp lệ
    if (!currentText && !initialText) return false;

    // Có text hợp lệ: kiểm tra các thay đổi khác
    return (
      text !== (initialNote || '') ||
      font !== (initialStyleConfig.font || fonts[0]) ||
      fontSize !== (initialStyleConfig.font_size || 40) ||
      color !== (initialStyleConfig.color || '#E9EAEB') ||
      strokeColor !== (initialStyleConfig.stroke_color || '#252B37') ||
      strokeWidth !== (initialStyleConfig.stroke_width || 10)
    );
  }, [
    text,
    font,
    fontSize,
    color,
    strokeColor,
    strokeWidth,
    initialNote,
    initialStyleConfig,
    fonts,
  ]);

  const handleSave = () => {
    if (!hasChanges) {
      onClose();
      return;
    }

    const styleConfig: NoteStyleConfig = {
      font,
      font_size: fontSize,
      color,
      stroke_color: strokeColor,
      stroke_width: strokeWidth,
    };

    onSave({
      note: text.trim(),
      styleConfig,
    });
    onClose();
  };

  const handleCancel = () => {
    if (hasChanges) {
      Modal.confirm({
        title: `${t('CONFIRM')}`,
        content: `${t('CONFIRM_DISCARD_CHANGES')}`,
        okText: `${t('CANCEL')}`,
        cancelText: `${t('RETURN')}`,
        centered: true,
        zIndex: 10001,
        onOk: () => {
          onClose();
        },
      });
    } else {
      onClose();
    }
  };

  const handleClose = () => {
    // Close directly without confirmation
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1000]"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleCancel();
      }}
    >
      <div
        className="bg-white w-[90%] h-[90%] md:w-[85%] lg:w-[70%] max-w-[1200px]
        max-h-[90vh] md:min-h-[75%] rounded-lg overflow-hidden shadow-xl flex flex-col"
      >
        <div className="flex justify-between items-center px-4 sm:px-6 py-3 border-b sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold">テキスト編集</h2>
          <button
            onClick={handleClose}
            className="text-[#666687] font-semibold hover:text-black text-[16px]"
          >
            閉じる
          </button>
        </div>

        <div className="flex-1 min-h-0 flex flex-col-reverse md:flex-row">
          {/* LEFT PANEL */}
          <div
            className="w-full h-[55vh] md:h-auto md:w-1/3 md:min-w-[280px] px-4 py-3 space-y-5
            border-b md:border-b-0 md:border-r border-gray-200
            overflow-y-auto"
          >
            {/* Text Input */}
            {!isNote ? (
              <div className="border-b-2 mb-5">
                <label className="block text-sm font-semibold mb-1">テキスト</label>
                <textarea
                  value={text}
                  disabled={true}
                  className="mb-10 w-full p-2 cursor-not-allowed border border-gray-300 rounded-md focus:ring outline-none resize-none"
                  rows={1}
                />
              </div>
            ) : (
              <div className="border-b-2 mb-5">
                <label className="block text-sm font-semibold mb-1">テキスト</label>
                <textarea
                  value={text}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setText(e.target.value)}
                  className="mb-10 w-full p-2 border border-gray-300 rounded-md focus:ring outline-none resize-none"
                  rows={3}
                />
              </div>
            )}

            {/* Font Selection */}
            <div
              className={`relative border-b-2 pb-15 ${isOpenFontDropDown ? 'z-50' : ''}`}
              ref={dropdownRef}
            >
              <label className="block text-sm font-semibold mb-1">フォント</label>

              {/* Selected font */}
              <div
                className="w-full p-2 border border-gray-300 rounded-md bg-white cursor-pointer flex justify-between items-center hover:border-gray-400 transition"
                onClick={() => setIsOpenFontDropDown((prev) => !prev)}
                aria-haspopup="listbox"
                aria-expanded={isOpenFontDropDown}
              >
                <span style={{ fontFamily: font }}>{font}</span>
                <span className="text-gray-500 text-sm">▼</span>
              </div>

              {/* Dropdown */}
              {isOpenFontDropDown && (
                <div className="absolute z-1000 bg-white border border-gray-300 rounded-md mt-1 w-full shadow-lg">
                  {/* Search input */}
                  <div className="relative p-2 border-b flex items-center">
                    <input
                      type="text"
                      placeholder="フォント検索"
                      value={searchFont}
                      onChange={(e) => setSearchFont(e.target.value)}
                      className="w-full relative p-2 pr-8 text-sm border border-gray-200 rounded-md outline-none focus:ring focus:ring-gray-200"
                    />
                    <div className="pointer-events-none absolute right-3">{searchFontIcon}</div>
                  </div>

                  {/* Font list */}
                  <ul className="max-h-60 overflow-y-auto" role="listbox">
                    {fonts
                      .filter((f) => f.toLowerCase().includes(searchFont.toLowerCase()))
                      .map((f, index) => (
                        <li
                          key={index}
                          onClick={() => {
                            setFont(f);
                            setIsOpenFontDropDown(false);
                          }}
                          role="option"
                          aria-selected={font === f}
                          className={`p-2  text-sm cursor-pointer hover:bg-gray-200 ${
                            font === f ? 'bg-[#F9F5FF]' : ''
                          }`}
                          style={{
                            fontFamily: f.includes('System') ? 'sans-serif' : f,
                          }}
                        >
                          <div className="flex justify-between items-center">
                            {f}
                            <p className="text-right">{f === font ? checkIcon2 : ''}</p>
                          </div>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
            {/* Font Size */}
            <div className="border-b-2 pb-5">
              <div className="flex justify-between items-center mb-5">
                <label className="block text-sm font-semibold mb-1">サイズ</label>
                <div className="text-md border-2 py-1 px-2 rounded-md">{fontSize}</div>
              </div>

              <input
                type="range"
                min="10"
                max={isNote ? 100 : 50}
                value={fontSize}
                onChange={(e) => setFontSize(Number(e.target.value))}
                className="w-full accent-[#666687] mb-10"
              />
            </div>
            {/* Text Color */}
            <div className="flex justify-between items-center border-b-2 pb-15 overflow-hidden">
              <label className="block text-sm font-semibold mb-1 flex-shrink-0">カラー</label>

              <div className="flex items-end justify-items-end gap-1">
                <div className="relative flex flex-row">
                  {!isMobileDevice ? (
                    <div
                      ref={colorButtonRef}
                      onClick={(e) => {
                        e.stopPropagation();

                        setShowColorPicker(!showColorPicker);
                      }}
                      className="relative w-8 h-8 rounded-full border
                       border-gray-400 cursor-pointer overflow-hidden"
                    >
                      <div
                        className="inset-0 rounded-full w-8 h-8 pr-10
                      
                      "
                        style={{
                          backgroundColor: color,
                          boxShadow: '0 0 3px rgba(0,0,0,0.2)',
                        }}
                      ></div>
                    </div>
                  ) : (
                    <label className="relative w-8 h-8 rounded-full border border-gray-400 cursor-pointer overflow-hidden">
                      <input
                        type="color"
                        value={color}
                        onChange={(e) => setColor(e.target.value)}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <span
                        className="absolute inset-0 rounded-full"
                        style={{
                          backgroundColor: color,
                          boxShadow: '0 0 3px rgba(0,0,0,0.2)',
                        }}
                      ></span>
                    </label>
                  )}

                  <input
                    type="text"
                    value={rgbToHex(color).toUpperCase()}
                    onChange={(e) => setColor(e.target.value)}
                    className="w-24 p-1 text-sm border ml-10
                     text-center border-gray-300 rounded-md"
                  />
                  {!isMobileDevice &&
                    showColorPicker &&
                    ReactDOM.createPortal(
                      <div
                        ref={colorPickerRef}
                        style={{
                          position: 'fixed',
                          top: colorPosition.top,
                          left: colorPosition.left,
                          zIndex: 99999,
                          background: 'white',
                          borderRadius: '8px',
                          boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                          padding: '8px',
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ColorPicker
                          value={color}
                          onChange={setColor}
                          disableDarkMode={true}
                          hidePresets={true}
                          hideEyeDrop={true}
                          hideAdvancedSliders={true}
                          hideInputType={true}
                          hideColorGuide={true}
                          hideGradientControls={true}
                          hideOpacity={false}
                          hideInputs={false}
                          hideGradientType={true}
                          hideColorTypeBtns={true}
                          hideControls={false}
                          showHexAlpha={true}
                          hideGradientAngle={true}
                          height={100}
                          width={200}
                        />
                      </div>,
                      document.body
                    )}
                </div>
              </div>
            </div>

            {/* Stroke */}
            <div className="flex justify-between items-center overflow-hidden">
              <div className="relative flex flex-col w-full">
                <div className="flex items-center justify-items-end justify-between gap-1">
                  <label className="block text-sm font-semibold mb-1 flex-shrink-0">
                    ストローク
                  </label>

                  <div className="flex flex-row mb-10">
                    {isMobileDevice ? (
                      <label className="relative w-8 h-8 rounded-full border border-gray-400 cursor-pointer overflow-hidden">
                        <input
                          type="color"
                          value={strokeColor}
                          onChange={(e) => setStrokeColor(e.target.value)}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        <span
                          className="absolute inset-0 rounded-full"
                          style={{
                            backgroundColor: strokeColor,
                            boxShadow: '0 0 3px rgba(0,0,0,0.2)',
                            border: `1px solid ${strokeColor}`,
                          }}
                        ></span>
                      </label>
                    ) : (
                      <div
                        ref={colorButtonStrokeRef}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowColorStrokePicker(!showColorStrokePicker);
                        }}
                        className="relative w-8 h-8 rounded-full border border-gray-400 cursor-pointer overflow-hidden"
                      >
                        <div
                          className="inset-0 rounded-full w-8 h-8 pr-10"
                          style={{
                            backgroundColor: strokeColor,
                            boxShadow: '0 0 3px rgba(0,0,0,0.2)',
                          }}
                        ></div>
                      </div>
                    )}

                    <input
                      type="text"
                      value={rgbToHex(strokeColor).toUpperCase()}
                      onChange={(e) => setStrokeColor(e.target.value)}
                      className="w-24 p-1 text-sm border ml-10
                     text-center border-gray-300 rounded-md"
                    />
                    <div className="text-base border-2 py-1 px-2 rounded-md ml-2 block md:hidden lg:block">
                      {strokeWidth}
                    </div>
                  </div>
                </div>

                <input
                  type="range"
                  min="0"
                  max="50"
                  value={strokeWidth}
                  onChange={(e) => setStrokeWidth(Number(e.target.value))}
                  className="w-full accent-[#666687] "
                />

                {!isMobileDevice &&
                  showColorStrokePicker &&
                  ReactDOM.createPortal(
                    <div
                      ref={colorPickerStrokeRef}
                      style={{
                        position: 'fixed',
                        top: strokePosition.top,
                        left: strokePosition.left,
                        zIndex: 99999,
                        background: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                        padding: '8px',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ColorPicker
                        value={strokeColor}
                        onChange={setStrokeColor}
                        disableDarkMode={true}
                        hidePresets={true}
                        hideEyeDrop={true}
                        hideAdvancedSliders={true}
                        hideInputType={true}
                        hideColorGuide={true}
                        hideOpacity={false}
                        hideInputs={false}
                        hideColorTypeBtns={true}
                        hideControls={false}
                        showHexAlpha={true}
                        height={100}
                        width={200}
                      />
                    </div>,
                    document.body
                  )}
              </div>
            </div>
          </div>

          {/* RIGHT PANEL */}
          <div ref={previewRef} className={previewContainerClass}>
            {isNote ? (
              /* ============================= MODE isNote ============================= */
              <>
                <img
                  ref={imgRef}
                  src={previewImage || demoImage}
                  alt="preview"
                  className="absolute inset-0 w-full h-full object-contain"
                  onLoad={(e) => {
                    const target = e.currentTarget;
                    setImgNaturalSize({ w: target.naturalWidth, h: target.naturalHeight });
                    if (previewRef.current) {
                      const rect = previewRef.current.getBoundingClientRect();
                      setPreviewSize({ w: rect.width, h: rect.height });
                    }
                  }}
                />

                {/* Overlay text nằm trong vùng ảnh hiển thị */}
                <div
                  className="absolute overflow-hidden"
                  style={{
                    left: `${displayedRect.left}px`,
                    top: `${displayedRect.top}px`,
                    width: `${displayedRect.width}px`,
                    height: `${displayedRect.height}px`,
                  }}
                >
                  <div
                    className="absolute inset-x-0 bottom-0 p-3"
                    style={{
                      fontFamily: font,
                      fontSize: `${scaledFont}px`,
                      color,
                      WebkitTextStroke: `${scaledStroke / 10}px ${strokeColor}`,
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      boxSizing: 'border-box',
                    }}
                  >
                    {text}
                  </div>
                </div>
              </>
            ) : (
              /* ============================= MODE Normal Text ============================= */
              <div className="relative w-full h-full flex flex-col items-center md:justify-center">
                {/* Vùng background phía trên */}
                {(() => {
                  const maxBannerHeight =
                    previewSize.h > 0
                      ? Math.min(scaledFont * 2.2, previewSize.h * 0.3)
                      : scaledFont * 2.2;
                  const bannerHeight = Math.max(50, maxBannerHeight);
                  const textMaxHeight = Math.max(38, bannerHeight - 12);
                  return (
                    <div
                      className="w-full relative overflow-hidden"
                      style={{
                        height: `${bannerHeight}px`,
                        backgroundImage: `url(${background})`,
                        backgroundSize: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        padding: '6px 6px',
                        width: resolvedPreviewWidth,
                        maxWidth: '100%',
                        minHeight: '50px',
                      }}
                    >
                      {/* Text nằm bên trong background */}
                      <div className="flex justify-between items-center w-full min-w-0">
                        <div className="flex flex-row gap-2 items-center text-center content-center flex-shrink-0">
                          <div>{menuIcon2}</div>
                          <p
                            className="text-white NotoSerifJPBold"
                            style={{ fontSize: `${scaledHostCollectionFont}px` }}
                          >
                            HOST COLLECTION
                          </p>
                        </div>
                        <div
                          style={{
                            maxWidth: '75%',
                            fontFamily: font,
                            fontSize: `${scaledFont}px`,
                            color,
                            WebkitTextStroke: `${scaledStroke / 10}px ${strokeColor}`,
                            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            boxSizing: 'border-box',
                            lineHeight: '1.2',
                            maxHeight: `${textMaxHeight}px`,
                          }}
                        >
                          {text}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <img
                  src={previewImage}
                  ref={imgRef}
                  className="w-full h-auto object-contain"
                  style={{ width: resolvedPreviewWidth, maxWidth: '100%' }}
                  alt="preview"
                  onLoad={(e) => {
                    const target = e.currentTarget;
                    setImgNaturalSize({ w: target.naturalWidth, h: target.naturalHeight });
                    if (previewRef.current) {
                      const rect = previewRef.current.getBoundingClientRect();
                      setPreviewSize({ w: rect.width, h: rect.height });
                    }
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* <div className="px-4 sm:px-6 py-3 border-t bg-gray-50 sticky bottom-0">
          <button
            onClick={handleSave}
            disabled={!hasChanges}
            className={`px-6 py-2 rounded-md font-medium ${
              hasChanges
                ? 'bg-[#666687] hover:bg-[#505061] text-white cursor-pointer'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Save
          </button>
        </div> */}
        <div className="px-4 sm:px-6 py-3 border-t bg-gray-50 sticky bottom-0">
          <button
            onClick={handleSave}
            disabled={!hasChanges}
            className={`w-full px-6 py-2 rounded-md font-medium transition-colors ${
              hasChanges
                ? 'bg-[#666687] hover:bg-[#505061] text-white cursor-pointer'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
};
export default ModalTextEditor;
export const FONTS = [
  'System',
  'NotoSansNormal',
  'NotoSerifJPBold',
  'NotoSansMedium',
  'NotoSansLight',
  'NotoSansThin',
  'NotoSansBlack',
  'NotoSansBold',
  'Hiragino',
  'BIZUDGothic',
  'BIZUDMincho',
  'BIZUDPGothic',
  'BIZUDPMincho',
  'DelaGothicOne',
  'HinaMincho',
  'IBMPlexSansJP',
  'KaiseiDecol',
  'KaiseiHarunoUmi',
  'KaiseiTokumin',
  'KiwiMaru',
  'Kosugi',
  'KosugiMaru',
  'MPLUS1',
  'MPLUS1p',
  'MPLUS2',
  'MPLUSRounded1c',
  'MochiPopOne',
  'MochiPopPOne',
  'Murecho',
  'NewTegomin',
  'SawarabiGothic',
  'ShipporiAntique',
  'ShipporiAntiqueB1',
  'ShipporiMincho',
  'YuseiMagic',
  'ZenAntique',
  'ZenMaruGothic',
  'ZenOldMincho',
];
